/**
 * AppShell — Training Portal sidebar layout (redesigned).
 *
 * Menu (merged from screenshot + existing routes):
 *   My Dashboard     → /home
 *   Learning Paths   → /learn
 *   Course Catalogue → /catalog
 *   My Certifications→ /history
 *   Compliance Training → /scenarios
 *   My Profile       → /profile
 *   ── divider ──
 *   Notifications    (badge, no dedicated page yet)
 *   Help & Support
 *   Logout
 *
 * Bottom: AI Assistant card (links to /drill/today) + language selector.
 */

import { useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { LanguageSelector } from './LanguageSelector';
import styles from './AppShell.module.css';

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function DashboardIcon() {
  return (
    <svg className={styles.navIcon} viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <rect x="2" y="2" width="6" height="6" rx="1.5" fill="currentColor" />
      <rect x="10" y="2" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.7" />
      <rect x="2" y="10" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.7" />
      <rect x="10" y="10" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.5" />
    </svg>
  );
}

function LearningPathsIcon() {
  return (
    <svg className={styles.navIcon} viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path d="M2 4h14M2 7h10M2 10h12M2 13h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function CatalogueIcon() {
  return (
    <svg className={styles.navIcon} viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <rect x="2" y="2" width="6" height="8" rx="1" stroke="currentColor" strokeWidth="1.5" />
      <rect x="10" y="2" width="6" height="8" rx="1" stroke="currentColor" strokeWidth="1.5" />
      <rect x="2" y="12" width="6" height="4" rx="1" stroke="currentColor" strokeWidth="1.5" />
      <rect x="10" y="12" width="6" height="4" rx="1" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function CertificationsIcon() {
  return (
    <svg className={styles.navIcon} viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.5" />
      <path d="M5.5 12.5l-1.5 4 5-2 5 2-1.5-4" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

function ComplianceIcon() {
  return (
    <svg className={styles.navIcon} viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path d="M9 2L3 5v5c0 3.5 2.5 6.5 6 7.5C12.5 16.5 15 13.5 15 10V5L9 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M6.5 9l2 2 3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ProfileIcon() {
  return (
    <svg className={styles.navIcon} viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <circle cx="9" cy="6" r="3.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M2.5 16c0-3.5 2.9-6 6.5-6s6.5 2.5 6.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function PolicyLabIcon() {
  return (
    <svg className={styles.navIcon} viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path d="M7 2h4v3l3 8a2 2 0 0 1-1.8 2.8H5.8A2 2 0 0 1 4 13l3-8V2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M7 2h4M5.5 11h7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function DrillIcon() {
  return (
    <svg className={styles.navIcon} viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <circle cx="9" cy="9" r="6.5" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="9" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M9 2.5v2M9 13.5v2M2.5 9h2M13.5 9h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function AssistantNavIcon() {
  return (
    <svg className={styles.navIcon} viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <rect x="2.5" y="6" width="13" height="9" rx="2.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M9 6V3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="9" cy="2.5" r="1" fill="currentColor" />
      <circle cx="6.5" cy="10.5" r="1" fill="currentColor" />
      <circle cx="11.5" cy="10.5" r="1" fill="currentColor" />
    </svg>
  );
}

function NotificationsIcon() {
  return (
    <svg className={styles.navIcon} viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path d="M9 2a5 5 0 0 1 5 5c0 5-2 6.5-2 6.5H5S3 12 3 7a5 5 0 0 1 5-5z" stroke="currentColor" strokeWidth="1.5" />
      <path d="M7.5 14.5a1.5 1.5 0 0 0 3 0" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function HelpIcon() {
  return (
    <svg className={styles.navIcon} viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="1.5" />
      <path d="M7 7c0-1.1.9-2 2-2s2 .9 2 2c0 1-1 1.5-2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="9" cy="13" r="0.75" fill="currentColor" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg className={styles.navIcon} viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path d="M7 3H4a1 1 0 00-1 1v10a1 1 0 001 1h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M12 6l3 3-3 3M15 9H7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function BotIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="8" width="18" height="12" rx="3" />
      <path d="M12 8V4" />
      <circle cx="12" cy="3" r="1" fill="white" stroke="none" />
      <circle cx="8.5" cy="14" r="1.5" fill="white" stroke="none" />
      <circle cx="15.5" cy="14" r="1.5" fill="white" stroke="none" />
      <path d="M9 18h6" strokeLinecap="round" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AppShell(): JSX.Element {
  const { t } = useTranslation('training');
  const { logout, refreshProfile } = useAuth();
  const navigate = useNavigate();

  // Re-sync the live profile once when the authenticated shell mounts, so
  // admin-side changes (e.g. an agent_level promotion) are reflected across
  // the portal — level-gated catalog, Learning Paths, Policy Lab — without
  // requiring the user to log out and back in.
  useEffect(() => {
    void refreshProfile();
  }, [refreshProfile]);

  function handleLogout(): void {
    logout();
    navigate('/', { replace: true });
  }

  function navClass({ isActive }: { isActive: boolean }): string {
    return `${styles.navItem} ${isActive ? styles.navItemActive : ''}`;
  }

  return (
    <div className={styles.layout}>
      {/* ------------------------------------------------------------------ */}
      {/* Sidebar                                                              */}
      {/* ------------------------------------------------------------------ */}
      <aside className={styles.sidebar} aria-label="Main navigation">
        {/* Brand */}
        <div className={styles.brand}>
          <div className={styles.brandLogoMark}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="7" height="7" rx="1.5" fill="white" />
              <rect x="14" y="3" width="7" height="7" rx="1.5" fill="white" opacity="0.7" />
              <rect x="3" y="14" width="7" height="7" rx="1.5" fill="white" opacity="0.7" />
              <rect x="14" y="14" width="7" height="7" rx="1.5" fill="white" opacity="0.5" />
            </svg>
          </div>
          <div className={styles.brandText}>
            <span className={styles.brandName}>ARIA</span>
            <span className={styles.brandTagline}>AI-Powered{'\n'}Insurance Learning{'\n'}Platform</span>
          </div>
        </div>

        {/* Primary nav */}
        <nav className={styles.nav}>
          {/* Learning */}
          <span className={styles.navSectionLabel}>{t('nav.learningLabel', 'Learning')}</span>

          <NavLink to="/home" className={navClass} end>
            <DashboardIcon />
            <span>{t('nav.home', 'My Dashboard')}</span>
          </NavLink>

          <NavLink to="/learn" className={navClass} end>
            <LearningPathsIcon />
            <span>{t('nav.learningPaths', 'Learning Paths')}</span>
          </NavLink>

          <NavLink to="/catalog" className={navClass}>
            <CatalogueIcon />
            <span>{t('nav.catalogue', 'Course Catalogue')}</span>
          </NavLink>

          <NavLink to="/history" className={navClass}>
            <CertificationsIcon />
            <span>{t('nav.certifications', 'My Certifications')}</span>
          </NavLink>

          <div className={styles.navDivider} />

          {/* AI Training modes */}
          <span className={styles.navSectionLabel}>{t('nav.aiTrainingLabel', 'AI Training')}</span>

          <NavLink to="/scenarios" className={navClass}>
            <ComplianceIcon />
            <span>{t('nav.training', 'AI Roleplay')}</span>
          </NavLink>

          <NavLink to="/policy-lab" className={navClass}>
            <PolicyLabIcon />
            <span>{t('nav.policyLab', 'Policy Lab')}</span>
          </NavLink>

          <NavLink to="/drill/today" className={navClass}>
            <DrillIcon />
            <span>{t('nav.drill', 'Daily Drill')}</span>
          </NavLink>

          <NavLink to="/assistant" className={navClass}>
            <AssistantNavIcon />
            <span>{t('nav.aiAssistant', 'AI Assistant')}</span>
          </NavLink>

          <div className={styles.navDivider} />

          {/* Account & utility */}
          <span className={styles.navSectionLabel}>{t('nav.accountLabel', 'Account')}</span>

          <NavLink to="/profile" className={navClass}>
            <ProfileIcon />
            <span>{t('nav.profile', 'My Profile')}</span>
          </NavLink>

          <button className={styles.navItem} type="button" aria-label="Notifications">
            <NotificationsIcon />
            <span>{t('nav.notifications', 'Notifications')}</span>
            <span className={styles.navBadge}>3</span>
          </button>

          <button
            className={styles.navItem}
            type="button"
            aria-label={t('nav.helpSupport', 'Help & Support')}
          >
            <HelpIcon />
            <span>{t('nav.helpSupport', 'Help & Support')}</span>
          </button>
        </nav>

        {/* Bottom section */}
        <div className={styles.sidebarBottom}>
          {/* AI Assistant card */}
          <div className={styles.aiAssistantCard}>
            <div className={styles.aiAssistantRow}>
              <div className={styles.aiAssistantIcon}><BotIcon /></div>
              <span className={styles.aiAssistantTitle}>{t('assistant.cardTitle', 'Ask AI Assistant')}</span>
            </div>
            <p className={styles.aiAssistantDesc}>
              {t('assistant.cardDesc', 'Get instant answers to your training questions and help.')}
            </p>
            <button
              className={styles.aiAssistantBtn}
              onClick={() => navigate('/assistant')}
              type="button"
            >
              {t('assistant.cardButton', 'Ask Now')}
            </button>
          </div>

          {/* Language selector */}
          <div className={styles.langRow}>
            <LanguageSelector />
          </div>

          <button
            className={styles.navItem}
            type="button"
            onClick={handleLogout}
            aria-label={t('nav.logoutLabel', 'Logout')}
          >
            <LogoutIcon />
            <span>{t('nav.logout', 'Logout')}</span>
          </button>
        </div>
      </aside>

      {/* ------------------------------------------------------------------ */}
      {/* Page content                                                         */}
      {/* ------------------------------------------------------------------ */}
      <main className={styles.content}>
        <Outlet />
      </main>
    </div>
  );
}
